import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { SurveyContext } from "../context/SurveyContext";
import { useNewsfeed } from "../context/NewsfeedContext";
import api from "../services/api";
import { useNotificationSocket } from "../context/SocketContext";
import { FaHome, FaSearch, FaBell, FaUser } from "react-icons/fa";

const BottomNav = ({ onToggleNotifications }) => {
  const { user } = useContext(AuthContext);
  const { condition } = useContext(SurveyContext);
  const { triggerRefresh } = useNewsfeed();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    let poll;
    const fetchUnread = async () => {
      try {
        if (!user || !condition) return;
        const { data } = await api.get("/notifications/unread-count", {
          params: { condition },
        });
        setUnreadCount(data.count || 0);
      } catch (e) {
        console.error("Failed to load unread notifications:", e);
      }
    };
    fetchUnread();
    poll = setInterval(fetchUnread, 30000);
    return () => clearInterval(poll);
  }, [user, condition]);

  // Listen for "mark all read" from NotificationPanel
  useEffect(() => {
    const handler = () => setUnreadCount(0);
    window.addEventListener("spiders:notifications-read", handler);
    return () => window.removeEventListener("spiders:notifications-read", handler);
  }, []);

  // Real-time badge increment from socket
  useNotificationSocket(
    React.useCallback((notification) => {
      if (notification.actorId !== user?.userId) {
        setUnreadCount((prev) => prev + 1);
      }
    }, [user?.userId])
  );

  if (!user) return null;

  const tabs = [
    {
      key: "home",
      icon: FaHome,
      path: "/newsfeed",
      label: "Home",
      onClick: () => {
        triggerRefresh();
        navigate("/newsfeed");
        window.scrollTo({ top: 0, behavior: "smooth" });
      },
    },
    {
      key: "search",
      icon: FaSearch,
      path: "/search",
      label: "Search",
      onClick: () => navigate("/search"),
    },
    {
      key: "notifications",
      icon: FaBell,
      path: "/notifications",
      label: "Alerts",
      badge: unreadCount,
      onClick: () => onToggleNotifications ? onToggleNotifications() : navigate("/notifications"),
    },
    {
      key: "profile",
      icon: FaUser,
      path: `/profile/${user.username}`,
      label: "Profile",
      onClick: () => navigate(`/profile/${user.username}`),
    },
  ];

  const isActive = (tab) => {
    if (tab.key === "home") return location.pathname === "/newsfeed";
    if (tab.key === "profile") return location.pathname.startsWith("/profile/");
    return location.pathname === tab.path;
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-spiders-bg-dark border-t border-spiders-border lg:hidden safe-bottom">
      <div className="flex justify-around items-center h-14">
        {tabs.map((tab) => {
          const active = isActive(tab);
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={tab.onClick}
              className="flex flex-col items-center justify-center flex-1 h-full relative transition-colors"
            >
              <div className="relative">
                <Icon
                  className={`w-5 h-5 ${
                    active
                      ? "text-spiders-primary-400"
                      : "text-spiders-text-muted"
                  }`}
                />
                {tab.badge > 0 && (
                  <span className="absolute -top-1.5 -right-2.5 bg-spiders-primary-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                    {tab.badge > 99 ? "99+" : tab.badge}
                  </span>
                )}
              </div>
              <span
                className={`text-[10px] mt-0.5 ${
                  active
                    ? "text-spiders-primary-400"
                    : "text-spiders-text-muted"
                }`}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
